/**
 * What the fill handle continues, rather than merely repeats.
 *
 * Dragging the little square at the corner of a selection is how a column
 * gets filled in Excel, and the whole of its usefulness is that it works out
 * what you meant: two cells reading 5 and 10 continue 15, 20, 25; a cell
 * reading "5 kg" continues "6 kg"; "Jan" continues "Feb". Until this existed
 * the drag repeated the source over and over, so a ledger filled by dragging
 * came out 5, 10, 5, 10 — which is not a series anybody wanted and is quietly
 * wrong rather than obviously broken.
 *
 * Only what the drag *writes* lives here. Formulas are not this module's
 * business: they are offset by the caller, which already knows how, and a
 * source containing one is declined outright so that path is left alone.
 */
import * as D from "./decimal.js";
/** Month names, long and short, as Excel continues them. */
const MONTHS_LONG = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const MONTHS_SHORT = MONTHS_LONG.map((name) => name.slice(0, 3));
const DAYS_LONG = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
const DAYS_SHORT = DAYS_LONG.map((name) => name.slice(0, 3));
const LISTS = [
    MONTHS_LONG,
    MONTHS_SHORT,
    DAYS_LONG,
    DAYS_SHORT,
];
/**
 * Work out what a run of cells is a series of, or null if it is not one.
 *
 * `inputs` are the source cells' raw inputs, in the order the drag runs.
 * Null means "no series here" and the caller should fall back to repeating
 * the source, which is also what Excel does with a block it cannot read.
 */
export function detectSeries(inputs) {
    if (inputs.length === 0)
        return null;
    // A formula is the caller's to offset; saying nothing here leaves that
    // path exactly as it was.
    if (inputs.some((input) => input.trim().startsWith('=')))
        return null;
    if (inputs.some((input) => input.trim() === ''))
        return null;
    return (numericSeries(inputs) ??
        listSeries(inputs) ??
        labelledNumberSeries(inputs) ??
        null);
}
// --- plain numbers -----------------------------------------------------------
/**
 * A run of numbers, continued by its own step.
 *
 * A single number is deliberately not a series: Excel copies a lone number
 * and only extends once you have given it two, because one number says
 * nothing about what comes next. Two or more, and the step is the slope of
 * the line through them — which for the ordinary evenly-spaced case is just
 * the difference, and for 1, 2, 4 gives what Excel's own trend gives.
 */
function numericSeries(inputs) {
    if (inputs.length < 2)
        return null;
    const numbers = inputs.map((input) => Number(input.trim()));
    if (numbers.some((value) => !Number.isFinite(value)))
        return null;
    // A number written with anything else in the cell is not this case.
    if (inputs.some((input) => !/^\s*-?[\d.]+\s*$/.test(input)))
        return null;
    const { slope, intercept } = fitLine(numbers);
    const decimals = Math.max(...inputs.map((input) => (input.split('.')[1] ?? '').length));
    return {
        at(index) {
            const value = intercept + slope * index;
            return decimals > 0 ? value.toFixed(decimals) : `${Math.round(value)}`;
        },
    };
}
/** Least squares through the points, which is the line Excel extends. */
function fitLine(values) {
    const n = values.length;
    const sumX = (n * (n - 1)) / 2;
    const sumY = values.reduce((total, value) => total + value, 0);
    const sumXY = values.reduce((total, value, index) => total + index * value, 0);
    const sumXX = values.reduce((total, _value, index) => total + index * index, 0);
    const denominator = n * sumXX - sumX * sumX;
    if (denominator === 0)
        return { slope: 0, intercept: values[0] ?? 0 };
    const slope = (n * sumXY - sumX * sumY) / denominator;
    return { slope, intercept: (sumY - slope * sumX) / n };
}
// --- month and day names -----------------------------------------------------
/**
 * "Jan" continuing to "Feb", and "Monday" to "Tuesday".
 *
 * One cell is enough, unlike a number: a month names its own position in a
 * list, so there is nothing to infer. A run of two or more may step by more
 * than one — Jan, Apr continues Jul — which is the same rule as the numbers
 * above applied to an index in the list.
 */
function listSeries(inputs) {
    for (const list of LISTS) {
        const positions = inputs.map((input) => list.findIndex((name) => name.toLowerCase() === input.trim().toLowerCase()));
        if (positions.some((position) => position === -1))
            continue;
        // The case the user typed, which is usually Title but sometimes not.
        const sample = inputs[0]?.trim() ?? '';
        const upper = sample === sample.toUpperCase();
        const lower = sample === sample.toLowerCase();
        const step = positions.length >= 2
            ? wrappedStep(positions[0], positions[1], list.length)
            : 1;
        const start = positions[0];
        return {
            at(index) {
                const at = (((start + step * index) % list.length) + list.length) % list.length;
                const name = list[at];
                return upper ? name.toUpperCase() : lower ? name.toLowerCase() : name;
            },
        };
    }
    return null;
}
/** The shorter way round a wrapping list — Dec to Jan is one, not eleven back. */
function wrappedStep(from, to, length) {
    const forward = (((to - from) % length) + length) % length;
    return forward === 0 ? length : forward;
}
/**
 * Split a cell into the number inside it and the words around it.
 *
 * The *last* run of digits is the one that counts, which is Excel's rule and
 * the one that makes "Invoice 2 of 5" continue sensibly. A leading number
 * works just as well, which is the case the client asked about: "5 kg".
 */
function labelled(input) {
    const match = /^(.*?)(\d+)(\D*)$/.exec(input);
    if (!match)
        return null;
    return { prefix: match[1] ?? '', digits: match[2] ?? '', suffix: match[3] ?? '' };
}
/**
 * "5 kg" continuing to "6 kg", and "5 kg", "10 kg" to "15 kg".
 *
 * One cell is enough here, unlike a bare number, and that asymmetry is
 * Excel's: a lone 5 is a quantity and copying it is the safe reading, while a
 * lone "5 kg" is an item in a list and the number in it is an index. Fifteen
 * years of ledgers are filled this way.
 *
 * The digits keep their width, so "Item 08" continues "Item 09" and then
 * "Item 10" rather than dropping to a single figure.
 */
function labelledNumberSeries(inputs) {
    /*
     * A bare number is not this case, and must not fall through to it: a lone
     * 5 is a quantity that Excel copies, while a lone "5 kg" is an item in a
     * list that it counts on. Without this guard the numeric rule above
     * declines a single number and this one increments it, which turns a column
     * of fives into 5, 6, 7, 8.
     */
    if (inputs.every((input) => /^\s*-?[\d.]+\s*$/.test(input)))
        return null;
    const parts = inputs.map(labelled);
    if (parts.some((part) => part === null))
        return null;
    const found = parts;
    const first = found[0];
    // Every cell has to be the same label, or this is a block of unrelated
    // text and repeating it is the honest answer.
    if (found.some((part) => part.prefix !== first.prefix || part.suffix !== first.suffix)) {
        return null;
    }
    const numbers = found.map((part) => Number(part.digits));
    const { slope, intercept } = numbers.length >= 2 ? fitLine(numbers) : { slope: 1, intercept: numbers[0] };
    const width = first.digits.length;
    const padded = first.digits.startsWith('0') && width > 1;
    return {
        at(index) {
            const value = Math.round(intercept + slope * index);
            // A series that would run below zero stops counting down rather than
            // writing a minus sign into the middle of a label.
            const digits = `${Math.max(0, value)}`;
            return `${first.prefix}${padded ? digits.padStart(width, '0') : digits}${first.suffix}`;
        },
    };
}
// --- dates -------------------------------------------------------------------
/**
 * Whether these cells are a run of date serials, and the step between them.
 *
 * Kept apart from the caller because a date is stored as a number here, so a
 * column of dates is indistinguishable from a column of five-figure
 * quantities without being told which it is. The caller knows, from the
 * cell's format; this only says what the step would be.
 */
export function dateSeriesStep(serials) {
    if (serials.length === 0)
        return null;
    if (serials.length === 1)
        return 1;
    const numbers = serials.map((value) => Number(D.toString(value)));
    if (numbers.some((value) => !Number.isFinite(value)))
        return null;
    const step = numbers[1] - numbers[0];
    for (let index = 2; index < numbers.length; index += 1) {
        if (numbers[index] - numbers[index - 1] !== step)
            return null;
    }
    return step;
}
//# sourceMappingURL=fill-series.js.map